import {
    AVERAGE_CHART_COLOR,
    CashflowChartProps,
    shouldShowAverages,
    SUPERCATEGORY_CONFIG,
} from '@/components/cashflow/charts/chartHelpers';
import SupercategoryTooltip from '@/components/cashflow/charts/SupercategoryTooltip';
import { useTransactionsChart } from '@/components/cashflow/charts/useTransactionsChart';
import {
    DEFAULT_MARGIN,
    ScaleNumeric,
} from '@/components/common/charts/chartHelper';
import CursorTracker from '@/components/common/charts/CursorTracker';
import DatesXAxis from '@/components/common/charts/DatesXAxis';
import { EntityLine } from '@/components/common/charts/EntityLine';
import NumericYAxisWithGrid from '@/components/common/charts/NumericYAxisWithGrid';
import { Circle } from '@/components/common/charts/shapes/shapes';
import { TodayLine } from '@/components/common/charts/TodayLine';
import VerticalGridLine from '@/components/common/charts/VerticalGridline';
import { FlexRow } from '@/components/common/Flex';
import { Box } from '@chakra-ui/react';
import { CashflowAmount, TransactionsChartDatum } from '@common/react/cashflow';
import { findMinMax } from '@common/util/arrays';
import { toDollars } from '@common/util/currency';
import { toPx } from '@common/util/styles';
import { isUndefined } from '@common/util/types';
import { nowTick, ValueSeries } from '@common/valueSeries';
import { app } from '@common/zapatos';
import { Group } from '@visx/group';
import React, { useCallback, useMemo } from 'react';

type Variant = 'complex' | 'simple';

export const SIMPLE_SPENDING_COLOR = '#E1908B';

const TransactionsLineChart = ({
    width,
    height,
    valueSeries,
    range,
    selectedSupercategories,
    // When enabled, renders the average spending line in light gray; currently used only in the dashboard
    showAverageSpending = false,
    variant = 'complex',
}: CashflowChartProps & {
    showAverageSpending?: boolean;
    // Complex variant includes axes and tooltips on hover, simple variant includes a legend and value tags, no hover
    variant?: Variant;
}) => {
    const { min, max } = useMinMax(
        valueSeries,
        selectedSupercategories,
        showAverageSpending
    );

    const margin =
        variant === 'simple'
            ? {
                  top: 10,
                  right: 10,
                  bottom: 20,
                  left: 10,
              }
            : DEFAULT_MARGIN;

    const {
        innerWidth,
        innerHeight,
        xScale,
        yScale,
        tooltip,
        todayX,
        handleCursor,
        handleCursorLeave,
        handleTooltipMouseEnter,
    } = useTransactionsChart({
        valueSeries,
        width,
        height,
        range,
        min,
        max,
        margin,
        xScaleType: 'point',
    });

    const xGetter = useCallback(
        (d: TransactionsChartDatum) => xScale(d.date) ?? 0,
        [xScale]
    );
    const yGetters = useYGetters(yScale);
    const today = nowTick(range.unit).date;
    const todayValue = valueSeries.values.find((v) => v.date === today);
    const todayOrLastDayValue =
        todayValue ?? valueSeries.values[valueSeries.values.length - 1];
    const valuesUntilToday = useMemo(
        () => valueSeries.values.filter((v) => v.date <= today),
        [valueSeries.values, today]
    );

    return (
        <FlexRow w={width} h={height} onMouseOut={handleCursorLeave}>
            <svg width={width} height={height}>
                <Group left={margin.left} top={margin.top}>
                    {variant === 'complex' && (
                        <>
                            <NumericYAxisWithGrid
                                scale={yScale}
                                width={innerWidth}
                                height={innerHeight}
                            />
                            <DatesXAxis
                                top={innerHeight}
                                scale={xScale}
                                unit={range.unit}
                            />
                        </>
                    )}

                    <TodayLine todayX={todayX} innerHeight={innerHeight} />
                    {selectedSupercategories.map((supercategory) => (
                        <EntityLine
                            key={supercategory}
                            solid
                            strokeWidth={2}
                            x={xGetter}
                            y={yGetters[supercategory].cumulative}
                            data={valuesUntilToday}
                            color={
                                variant === 'simple' &&
                                supercategory === 'SPENDING'
                                    ? SIMPLE_SPENDING_COLOR
                                    : SUPERCATEGORY_CONFIG[supercategory].color
                            }
                            yScale={yScale}
                        />
                    ))}
                    {showAverageSpending && (
                        <EntityLine
                            strokeWidth={2}
                            x={xGetter}
                            y={yGetters.SPENDING.cumulativeAvg}
                            data={valueSeries.values}
                            color={AVERAGE_CHART_COLOR}
                            yScale={yScale}
                            gradientAlphaMax={0}
                        />
                    )}
                    {shouldShowAverages(selectedSupercategories) && (
                        <EntityLine
                            strokeWidth={2}
                            x={xGetter}
                            y={
                                yGetters[selectedSupercategories[0]]
                                    .cumulativeAvg
                            }
                            data={valueSeries.values}
                            color={AVERAGE_CHART_COLOR}
                            yScale={yScale}
                            gradientAlphaMax={0}
                        />
                    )}
                    {tooltip.visible && tooltip.data && (
                        <>
                            <VerticalGridLine
                                x={tooltip.midpointX}
                                height={innerHeight}
                            />
                            <LineMarkers
                                x={tooltip.midpointX}
                                todayX={todayX}
                                datum={tooltip.data}
                                yScale={yScale}
                                selectedSupercategories={
                                    selectedSupercategories
                                }
                                variant={variant}
                                showAverageSpending={showAverageSpending}
                            />
                        </>
                    )}
                    {!isUndefined(todayX) && todayValue && (
                        <LineMarkers
                            x={todayX}
                            todayX={todayX}
                            datum={todayValue}
                            yScale={yScale}
                            selectedSupercategories={selectedSupercategories}
                            variant={variant}
                            showAverageSpending={showAverageSpending}
                        />
                    )}
                    {variant === 'complex' && (
                        <CursorTracker
                            w={innerWidth}
                            h={innerHeight}
                            onCursor={handleCursor}
                            onCursorLeave={handleCursorLeave}
                        />
                    )}
                </Group>
            </svg>
            {variant === 'simple' && (
                <>
                    <ValueTag
                        amount={todayOrLastDayValue.SPENDING.cumulativeAvg}
                        top={
                            yScale(
                                -todayOrLastDayValue.SPENDING.cumulativeAvg
                            ) - 2
                        }
                        color="gray.600"
                        bg="gray.100"
                        right={todayX ? width - todayX : 0}
                    />
                    <ValueTag
                        amount={todayOrLastDayValue.SPENDING.cumulative}
                        color="white"
                        bg={
                            todayOrLastDayValue.SPENDING.cumulative <
                            todayOrLastDayValue.SPENDING.cumulativeAvg
                                ? SIMPLE_SPENDING_COLOR
                                : 'black'
                        }
                        top={
                            yScale(-todayOrLastDayValue.SPENDING.cumulative) - 2
                        }
                        left={todayX ? todayX + 20 : undefined}
                        right={todayX ? undefined : 0}
                    />
                </>
            )}
            <SupercategoryTooltip
                tooltip={tooltip}
                today={today}
                selectedSupercategories={selectedSupercategories}
                primaryKey="cumulative"
                averageKey="cumulativeAvg"
                onMouseEnter={handleTooltipMouseEnter}
                unit={range.unit}
                showAverageSpending={showAverageSpending}
            />
        </FlexRow>
    );
};

const ValueTag = ({
    amount,
    top,
    right,
    left,
    color,
    bg,
}: {
    amount: number;
    top: number;
    color: string;
    bg: string;
    right?: number;
    left?: number;
}) => (
    <Box
        position="absolute"
        top={top}
        right={right && toPx(right)}
        left={left && toPx(left)}
        fontSize="xs"
        color={color}
        fontWeight="medium"
        py={1}
        px={2}
        bg={bg}
        borderRadius="md"
    >
        {toDollars(amount)}
    </Box>
);

const LineMarkers = ({
    x,
    todayX,
    datum,
    yScale,
    selectedSupercategories,
    showAverageSpending,
    variant,
}: {
    x: number;
    todayX: number | undefined;
    datum: TransactionsChartDatum;
    yScale: ScaleNumeric;
    selectedSupercategories: app.supercategory[];
    showAverageSpending: boolean;
    variant: Variant;
}) => (
    <>
        {shouldShowAverages(selectedSupercategories) && (
            <Circle
                x={x}
                y={yScale(
                    datum[selectedSupercategories[0]].cumulativeAvg *
                        getMultiplier(selectedSupercategories[0])
                )}
                color={AVERAGE_CHART_COLOR}
            />
        )}
        {showAverageSpending && (
            <Circle
                x={x}
                y={yScale(-datum.SPENDING.cumulativeAvg)}
                color={AVERAGE_CHART_COLOR}
            />
        )}
        {(isUndefined(todayX) || x <= todayX) &&
            selectedSupercategories.map((supercategory) => {
                const Component =
                    SUPERCATEGORY_CONFIG[supercategory].shapeComponent;
                return (
                    <Component
                        key={supercategory}
                        x={x}
                        y={yScale(
                            datum[supercategory].cumulative *
                                getMultiplier(supercategory)
                        )}
                        color={
                            variant === 'simple' && supercategory === 'SPENDING'
                                ? SIMPLE_SPENDING_COLOR
                                : SUPERCATEGORY_CONFIG[supercategory].color
                        }
                    />
                );
            })}
    </>
);

const useYGetters = (
    yScale: ScaleNumeric
): Record<
    app.supercategory,
    {
        cumulative: (datum: TransactionsChartDatum) => number;
        cumulativeAvg: (datum: TransactionsChartDatum) => number;
    }
> => {
    const getYGetter = useCallback(
        (supercategory: app.supercategory, amountType: keyof CashflowAmount) =>
            (datum: TransactionsChartDatum) => {
                const amounts = datum[supercategory] as CashflowAmount;
                const amount = amounts[amountType];
                return yScale(amount * getMultiplier(supercategory));
            },
        [yScale]
    );

    return {
        SPENDING: {
            cumulative: getYGetter('SPENDING', 'cumulative'),
            cumulativeAvg: getYGetter('SPENDING', 'cumulativeAvg'),
        },
        INCOME: {
            cumulative: getYGetter('INCOME', 'cumulative'),
            cumulativeAvg: getYGetter('INCOME', 'cumulativeAvg'),
        },
        SAVING_INVESTING: {
            cumulative: getYGetter('SAVING_INVESTING', 'cumulative'),
            cumulativeAvg: getYGetter('SAVING_INVESTING', 'cumulativeAvg'),
        },
        TRANSFERS: {
            cumulative: getYGetter('TRANSFERS', 'cumulative'),
            cumulativeAvg: getYGetter('TRANSFERS', 'cumulativeAvg'),
        },
    };
};

const useMinMax = (
    valueSeries: ValueSeries<TransactionsChartDatum>,
    selectedSupercategories: app.supercategory[],
    showAverageSpending: boolean
) =>
    useMemo(
        () =>
            findMinMax(valueSeries.values, (v) =>
                selectedSupercategories.flatMap((supercategory) => {
                    const amounts = v[supercategory];
                    const multiplier = getMultiplier(supercategory);

                    return [
                        amounts.cumulative * multiplier,
                        ...(shouldShowAverages(selectedSupercategories) ||
                        (supercategory === 'SPENDING' && showAverageSpending)
                            ? // We only want to show the average line when one supercategory is selected
                              [amounts.cumulativeAvg * multiplier]
                            : []),
                    ];
                })
            ),
        [showAverageSpending, selectedSupercategories, valueSeries.values]
    );

// Spending values are negative, but we show them on the chart as positive
const getMultiplier = (supercategory: app.supercategory) =>
    supercategory === 'SPENDING' ? -1 : 1;

export default TransactionsLineChart;
